// Acompanha a reprodução da aula: retoma de onde o aluno parou, mostra o tempo
// corrente e reporta posição e duração ao servidor.
//
// Conversa com o player por `postMessage`, e não pelo SDK do provedor. Carregar
// o iframe_api do YouTube (ou o player.js do Vimeo) obrigaria a abrir o
// `script-src` do CSP para os domínios deles — e ainda assim só resolveria
// esses dois. O protocolo de mensagens é o mesmo que os SDKs usam por baixo,
// custa nada e mantém a política fechada.
//
// Cobertura: mídia direta (<video>/<audio>), YouTube e Vimeo. Outro provedor
// continua tocando normalmente — apenas sem posição registrada, e o rótulo
// simplesmente não aparece.
const SAVE_EVERY_MS = 15000 // enquanto toca
const MIN_DELTA = 3 // segundos: não grava mexida irrelevante
const MIN_RESUME = 5 // abaixo disso não vale retomar
const TAIL = 10 // nem retomar no finzinho da aula

// Salto máximo, em segundos de conteúdo, que ainda conta como reprodução
// contínua entre duas amostras. Acima disso o aluno navegou (arrastou a barra,
// pulou 10s) e o trecho pulado não foi assistido.
//
// Cinco e não dois por causa da velocidade: a 2× o tempo do conteúdo anda o
// dobro entre amostras, e um teto apertado quebraria a leitura de quem assiste
// acelerado — justamente quem mais repete trecho.
const CONTINUOUS_MAX = 5

// Teto do trecho que fica aberto na memória do navegador antes de ser cortado
// e enviado. Cinco minutos: perder mais que isso numa aba fechada à força seria
// demais, e uma emenda a cada cinco minutos é ruído desprezível.
const MAX_OPEN = 300

function initLearningPlayer() {
    const stage = document.querySelector("[data-player]")
    if (!stage) return

    const url = stage.dataset.playerUrl
    const label = document.querySelector("[data-player-label]")
    const token = document.querySelector("meta[name=csrf-token]")?.content ?? ""

    // `?t=125` manda o player para um segundo específico — é o que faz o atalho
    // de "onde você travou" abrir a aula no trecho, e não no começo. Quando não
    // vem na URL, vale a posição guardada do aluno.
    const deepLink = Number(new URLSearchParams(location.search).get("t") || 0)
    const startAt = deepLink > 0 ? deepLink : Number(stage.dataset.playerPosition || 0)

    let position = startAt
    let duration = Number(stage.dataset.playerDuration || 0)
    let playing = false
    let saved = startAt
    let resumed = false
    let timer = null

    // Trechos percorridos desde o último envio. `open` é o intervalo que está
    // crescendo agora; ele fecha quando o aluno pausa, salta ou o relatório vai
    // embora. Voltar e assistir de novo abre um intervalo novo sobre o mesmo
    // pedaço — e é isso que faz o contador do trecho subir no servidor.
    let watched = []
    let open = null
    let rate = 1

    // O avanço que conclui a aula. Nasce escondido e aparece quando a
    // reprodução alcança a fração definida pela empresa — quem sabe onde a
    // aula está é este arquivo, então é ele quem revela.
    const gate = document.querySelector("[data-completion-gate]")
    const threshold = Number(stage.dataset.playerThreshold || 0)
    const eventUrl = stage.dataset.playerEventUrl

    const media = stage.querySelector("video, audio")
    const frame = stage.querySelector("iframe")

    if (media) watchMedia(media)
    else if (frame) watchFrame(frame)
    else return

    render()
    window.addEventListener("pagehide", () => save({ beacon: true }))
    document.addEventListener("visibilitychange", () => {
        if (document.visibilityState === "hidden") save({ beacon: true })
    })

    // --- envio -------------------------------------------------------------

    function save({ beacon = false } = {}) {
        // Saindo da página não haverá outra chance: o que está aberto fecha aqui.
        if (beacon) closeOpen()
        const ranges = flushWatched()
        if (!url) return
        // Sem trecho novo, só vale enviar se a posição andou de verdade.
        if (!ranges && (!position || Math.abs(position - saved) < MIN_DELTA)) return
        saved = position

        const body = new FormData()
        body.append("position_seconds", Math.floor(position))
        if (duration) body.append("duration_seconds", Math.floor(duration))
        if (ranges) body.append("ranges", ranges)
        if (rate) body.append("playback_rate", rate.toFixed(2))

        // Ao sair da página só o sendBeacon é garantido, e ele não permite
        // cabeçalhos — daí o token e o método irem no corpo.
        if (beacon && navigator.sendBeacon) {
            body.append("authenticity_token", token)
            body.append("_method", "patch")
            navigator.sendBeacon(url, body)
            return
        }

        fetch(url, {
            method: "PATCH",
            headers: { "X-CSRF-Token": token },
            body,
            keepalive: true,
        }).catch(() => {}) // perder um relatório de posição não é erro de tela
    }

    // Avisa o servidor que a reprodução começou ou parou, com o PONTO DA AULA em
    // que isso aconteceu — "deu play no minuto 3", "parou no minuto 7".
    //
    // Uma vez por ação: parar e voltar é outro play, porque é isso que a
    // pergunta "quantas vezes ele deu play" quer saber. A repetição imediata
    // (provedor reemitindo o estado) é descartada do outro lado.
    function reportPlayback(kind) {
        if (!eventUrl) return

        const body = new FormData()
        body.append("kind", kind)
        body.append("position_seconds", Math.floor(position))

        fetch(eventUrl, {
            method: "POST",
            headers: { "X-CSRF-Token": token },
            body,
            keepalive: true,
        }).catch(() => {}) // perder um registro destes não é erro de tela
    }

    function startTicking() {
        if (timer) return
        timer = setInterval(() => save(), SAVE_EVERY_MS)
    }

    function stopTicking() {
        clearInterval(timer)
        timer = null
    }

    // --- estado ------------------------------------------------------------

    function update({ at, total, isPlaying, speed }) {
        if (typeof speed === "number" && speed > 0) rate = speed
        if (typeof total === "number" && total > 0) duration = total
        if (typeof at === "number" && at >= 0) {
            trace(at)
            position = at
        }
        if (typeof isPlaying === "boolean") {
            const changed = isPlaying !== playing
            playing = isPlaying
            playing ? startTicking() : stopTicking()
            // Só a MUDANÇA vira evento: o provedor reafirma o estado atual a
            // toda amostra, e sem isto cada segundo de reprodução seria um play.
            if (changed) reportPlayback(isPlaying ? "played" : "paused")
            if (!playing) {
                closeOpen()
                save()
            }
        }
        render()
    }

    // Acompanha a linha do tempo amostra a amostra e vai montando os trechos
    // efetivamente assistidos.
    function trace(at) {
        if (!playing) return

        const step = at - position
        // Avanço curto: continua o mesmo trecho. Salto (ou volta): o anterior
        // fecha aqui e um novo começa onde o aluno caiu.
        if (open && step > 0 && step <= CONTINUOUS_MAX) {
            open[1] = at
            return
        }

        closeOpen()
        open = [at, at]
    }

    function closeOpen() {
        // Trecho de duração nula não é reprodução — é um seek que não rendeu.
        if (open && open[1] > open[0]) watched.push(open)
        open = null
    }

    // Serializa e limpa os trechos JÁ FECHADOS. O que ainda está sendo assistido
    // continua aberto entre um envio e outro: cortá-lo a cada 15s faria o bloco
    // da emenda contar duas vezes numa reprodução que foi contínua.
    //
    // O corte só acontece quando o trecho aberto fica longo demais — aí vale
    // mais garantir o registro do que preservar a emenda perfeita.
    function flushWatched() {
        if (open && open[1] - open[0] >= MAX_OPEN) {
            watched.push(open)
            open = [open[1], open[1]]
        }
        if (!watched.length) return null

        const ranges = joinContiguous(watched)
        watched = []
        return ranges.map(([from, to]) => `${from.toFixed(1)}-${to.toFixed(1)}`).join(",")
    }

    // Junta trechos que se ENCOSTAM (pausou e voltou no mesmo ponto), nunca os
    // que se sobrepõem: sobreposição é o aluno rebobinando e assistindo de novo,
    // que é exatamente a repetição a ser contada — fundir apagaria o dado.
    function joinContiguous(ranges) {
        const out = []

        ranges.forEach((range) => {
            const last = out[out.length - 1]
            if (last && Math.abs(range[0] - last[1]) <= 0.5) last[1] = Math.max(last[1], range[1])
            else out.push(range.slice())
        })

        return out
    }

    // Retoma uma única vez, e só quando faz sentido: no começo não há o que
    // retomar, e no fim retomar seria devolver o aluno para os créditos.
    function shouldResume() {
        if (resumed || startAt < MIN_RESUME) return false
        if (duration && startAt > duration - TAIL) return false
        resumed = true
        return true
    }

    // Revela o avanço uma única vez, quando o aluno chega ao fim combinado.
    //
    // Vale também na volta: quem parou depois do limiar ontem encontra o botão
    // já visível hoje, porque a posição guardada entra na conta antes de o
    // player reportar qualquer coisa.
    function maybeRevealGate() {
        if (!gate || !gate.hidden || !threshold) return
        if (!duration || duration <= 0) return
        if (position / duration < threshold) return

        gate.hidden = false
    }

    function render() {
        maybeRevealGate()
        if (!label) return
        if (!position) return (label.textContent = "")

        const time = duration ? `${clock(position)} de ${clock(duration)}` : clock(position)
        label.textContent = playing ? `Assistindo · ${time}` : `Você parou em ${time}.`
    }

    function clock(seconds) {
        const total = Math.floor(seconds)
        const s = String(total % 60).padStart(2, "0")
        const m = Math.floor(total / 60) % 60
        const h = Math.floor(total / 3600)
        return h ? `${h}:${String(m).padStart(2, "0")}:${s}` : `${String(m).padStart(2, "0")}:${s}`
    }

    // --- <video> e <audio> -------------------------------------------------

    function watchMedia(el) {
        el.addEventListener("loadedmetadata", () => {
            update({ total: el.duration })
            if (shouldResume()) el.currentTime = startAt
        })
        el.addEventListener("timeupdate", () => update({ at: el.currentTime, speed: el.playbackRate }))
        el.addEventListener("play", () => update({ isPlaying: true }))
        el.addEventListener("pause", () => update({ isPlaying: false }))
        el.addEventListener("ended", () => update({ at: el.duration, isPlaying: false }))
    }

    // --- embeds ------------------------------------------------------------

    function watchFrame(el) {
        const src = el.getAttribute("src") || ""
        if (src.includes("youtube.com/embed/")) return watchYouTube(el)
        if (src.includes("player.vimeo.com/")) return watchVimeo(el)
        // Provedor sem canal conhecido: a aula toca, mas não há o que escutar.
    }

    function watchYouTube(el) {
        const origin = "https://www.youtube.com"
        const post = (message) => el.contentWindow?.postMessage(JSON.stringify(message), origin)

        // O player só passa a emitir depois que alguém se apresenta. Como não há
        // evento de "iframe pronto para conversar", a apresentação é repetida
        // até a primeira resposta chegar.
        const hello = setInterval(() => post({ event: "listening", id: 1, channel: "widget" }), 500)
        el.addEventListener("load", () => post({ event: "listening", id: 1, channel: "widget" }))

        window.addEventListener("message", (event) => {
            if (!event.origin.endsWith("youtube.com") || event.source !== el.contentWindow) return

            const data = parse(event.data)
            const info = data?.info
            if (!info) return
            clearInterval(hello)

            // 1 = tocando, 2 = pausado, 0 = terminou.
            const state = info.playerState
            update({
                at: info.currentTime,
                total: info.duration,
                // O YouTube manda a velocidade junto com a posição; o Vimeo não
                // devolve isso no timeupdate, então ali ela fica em 1×.
                speed: info.playbackRate,
                isPlaying: state === undefined ? undefined : state === 1,
            })

            if (shouldResume()) post({ event: "command", func: "seekTo", args: [startAt, true] })
        })
    }

    function watchVimeo(el) {
        const origin = "https://player.vimeo.com"
        const post = (message) => el.contentWindow?.postMessage(message, origin)

        const hello = setInterval(() => {
            post({ method: "addEventListener", value: "timeupdate" })
            post({ method: "addEventListener", value: "play" })
            post({ method: "addEventListener", value: "pause" })
        }, 500)

        window.addEventListener("message", (event) => {
            if (event.origin !== origin || event.source !== el.contentWindow) return

            const data = parse(event.data)
            if (!data) return
            clearInterval(hello)

            if (data.event === "timeupdate") {
                update({ at: data.data?.seconds, total: data.data?.duration })
                if (shouldResume()) post({ method: "setCurrentTime", value: startAt })
            }
            if (data.event === "play") update({ isPlaying: true })
            if (data.event === "pause") update({ isPlaying: false })
        })
    }

    // Os dois provedores enviam ora string JSON, ora objeto.
    function parse(payload) {
        if (typeof payload === "object") return payload
        try {
            return JSON.parse(payload)
        } catch {
            return null
        }
    }
}

document.addEventListener("DOMContentLoaded", initLearningPlayer)
